import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Save, Loader2, CheckCircle2, Paperclip, ExternalLink, ShoppingBag } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import { toast } from "sonner";

const STATUS_OPTIONS = ["pendente", "em_analise", "aprovado", "pago", "rejeitado"];

export default function AdminReembolsoDetail() {
  const { numero } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const [orderLookup, setOrderLookup] = useState(null); // {numero: match|null}

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/reembolsos/${numero}`);
      setItem(data);
      setStatus(data.status);
      setNotas(data.notas || "");
      // Fire-and-forget lookup of Shopify orders for the refund's purchase numbers
      if (Array.isArray(data.numeros_compra) && data.numeros_compra.length) {
        try {
          const { data: lk } = await api.post("/admin/encomendas/lookup", {
            numeros: data.numeros_compra,
          });
          setOrderLookup(lk.matches || {});
        } catch (_e) {
          setOrderLookup({});
        }
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numero]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/reembolsos/${numero}`, { status, notas });
      setItem(data);
      toast.success("Processo atualizado com sucesso.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const copyIban = () => {
    if (!item) return;
    navigator.clipboard.writeText(item.iban);
    toast.success("IBAN copiado.");
  };

  if (loading) {
    return <div className="text-[var(--ldr-text-muted)]" data-testid="detail-loading">A carregar…</div>;
  }
  if (error || !item) {
    return (
      <div className="ldr-card p-6" data-testid="detail-error">
        <p className="text-[var(--ldr-red)]">{error || "Processo não encontrado."}</p>
        <Link to="/admin/reembolsos" className="ldr-btn-outline mt-4 inline-flex">
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="admin-reembolso-detail">
      <div className="flex items-center justify-between gap-4 mb-6">
        <Link to="/admin/reembolsos" className="ldr-btn-outline" data-testid="detail-back">
          <ArrowLeft size={14} /> Voltar
        </Link>
        <StatusBadge status={item.status} />
      </div>

      <header className="mb-8">
        <p className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)]">Processo</p>
        <h1 className="mt-1 text-3xl font-bold text-[var(--ldr-red)] tracking-wide" data-testid="detail-numero">
          {item.numero_processo}
        </h1>
        <p className="mt-2 text-sm text-[var(--ldr-text-muted)]">
          Submetido em {formatDate(item.created_at)} · Atualizado em {formatDate(item.updated_at)}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 ldr-card p-6" data-testid="detail-info">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-[var(--ldr-text-muted)] mb-4">
            Informação do cliente
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <Field label="Nome" value={item.nome} testid="field-nome" />
            <Field label="Email" value={<a href={`mailto:${item.email}`}>{item.email}</a>} testid="field-email" />
            <Field label="Telefone" value={<a href={`tel:${item.telefone}`}>{item.telefone}</a>} testid="field-telefone" />
            <Field
              label="IBAN"
              value={
                <div className="flex items-center gap-2">
                  <span className="font-mono">{item.iban}</span>
                  <button
                    type="button"
                    onClick={copyIban}
                    className="text-[var(--ldr-text-muted)] hover:text-[var(--ldr-red)]"
                    aria-label="Copiar IBAN"
                    data-testid="copy-iban"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              }
              testid="field-iban"
            />
          </dl>

          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)] mb-2">
              Número(s) de compra
            </div>
            <ul className="space-y-2" data-testid="field-numeros-compra">
              {item.numeros_compra.map((n, i) => {
                const match = orderLookup ? orderLookup[n] : undefined;
                return (
                  <li
                    key={i}
                    className="bg-[var(--ldr-bg-alt)] border border-[var(--ldr-border)] rounded px-3 py-2"
                    data-testid={`numero-compra-${i}`}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="font-mono text-sm">{n}</span>
                      {orderLookup === null ? null : match ? (
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-xs font-semibold uppercase tracking-wider text-[#1E6B33]">
                            Encomenda localizada
                          </span>
                          <span className="text-xs text-[var(--ldr-text-muted)]">
                            {match.name} · {(match.financial_status || "—").toUpperCase()}
                            {match.total != null && (
                              <> · {new Intl.NumberFormat("pt-PT", {
                                style: "currency",
                                currency: match.currency || "EUR",
                              }).format(match.total)}</>
                            )}
                          </span>
                          <Link
                            to={`/admin/encomendas/${encodeURIComponent((match.name || "").replace(/^#/, ""))}`}
                            className="text-xs font-semibold text-[var(--ldr-red)] inline-flex items-center gap-1"
                            data-testid={`ver-encomenda-${i}`}
                          >
                            <ShoppingBag size={12} /> Ver encomenda
                          </Link>
                        </div>
                      ) : (
                        <span className="text-xs uppercase tracking-wider text-[var(--ldr-text-muted)]"
                              data-testid={`encomenda-nao-localizada-${i}`}>
                          Encomenda não localizada
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {item.attachments && item.attachments.length > 0 && (
            <div className="mt-6" data-testid="detail-attachments">
              <div className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)] mb-2 flex items-center gap-1.5">
                <Paperclip size={12} /> Comprovativos ({item.attachments.length})
              </div>
              <ul className="space-y-2">
                {item.attachments.map((a) => (
                  <li key={a.id} data-testid={`attachment-${a.id}`}>
                    <a
                      href={`${BACKEND_URL}/api/admin/reembolsos/${item.numero_processo}/attachments/${a.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-3 bg-[var(--ldr-bg-alt)] border border-[var(--ldr-border)] rounded px-3 py-2 text-sm hover:border-[var(--ldr-red)] transition-colors"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="truncate text-[var(--ldr-text)]">{a.filename}</span>
                        <span className="text-xs text-[var(--ldr-text-muted)] shrink-0">
                          {(a.size / 1024).toFixed(0)} KB
                        </span>
                      </span>
                      <ExternalLink size={14} className="shrink-0 text-[var(--ldr-red)]" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.historico && item.historico.length > 0 && (
            <div className="mt-8" data-testid="detail-historico">
              <div className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)] mb-3">
                Histórico do processo
              </div>
              <ol className="relative border-l border-[var(--ldr-border)] ml-2 space-y-4">
                {[...item.historico].reverse().map((h, i) => (
                  <li key={i} className="pl-4 relative" data-testid={`historico-entry-${i}`}>
                    <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-[var(--ldr-red)] border-2 border-white" />
                    <div className="text-sm text-[var(--ldr-text)]">
                      {(h.changes || []).map((c, ci) => (
                        <div key={ci}>{c}</div>
                      ))}
                    </div>
                    <div className="text-xs text-[var(--ldr-text-muted)] mt-1">
                      {h.by_email || h.by_name || "sistema"} · {formatDate(h.at)}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>

        <aside className="ldr-card p-6 h-fit" data-testid="detail-actions">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-[var(--ldr-text-muted)] mb-4">
            Gerir processo
          </h2>

          <div>
            <label className="ldr-label" htmlFor="d-status">Estado</label>
            <select
              id="d-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="ldr-input"
              data-testid="detail-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            <label className="ldr-label" htmlFor="d-notas">Notas internas</label>
            <textarea
              id="d-notas"
              rows={5}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              className="ldr-input resize-y"
              placeholder="Anotações visíveis apenas para a equipa…"
              data-testid="detail-notas"
            />
          </div>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="ldr-btn-primary w-full mt-5"
            data-testid="detail-save"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={14} />}
            {saving ? "A guardar…" : "Guardar alterações"}
          </button>

          <p className="text-xs text-[var(--ldr-text-muted)] mt-4 leading-relaxed inline-flex items-start gap-2">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-[#1E6B33]" />
            As alterações são registadas no processo e não são enviadas ao cliente automaticamente.
          </p>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <dt className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)]">{label}</dt>
      <dd className="mt-1 text-[var(--ldr-text)] break-words">{value}</dd>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
  } catch (_e) {
    return iso;
  }
}
